/**
 * WorldSerialization - converts game objects to/from JSON. Handles constructor
 * mapping, validation schemas, and scene hierarchy reconstruction.
 */

import { SceneComponent } from '../components/SceneComponent.js';
import { CurrentSerializationVersion } from '../const.js';
import { Level } from '../game/Level.js';
import { createDumper, createLoader } from '../utils/serialization/factories.js';
import { loadPrefabTemplateInstance } from '../utils/serialization/prefab.js';
import { PrefabManager } from '../utils/serialization/prefabManager.js';
import { DumperFlags, isLegacyData, LoaderFlags } from '../utils/serialization/types.js';
import { replace_uuids } from '../utils/uuid.js';

import { ClassRegistry } from './ClassRegistry.js';
import {
  cloneActorWithNewUUIDs,
  cloneForDuplication,
  collectAssetPathMatches,
  collectClassNameMatches,
  collectPrefabPathsFromSerializedData,
  readRootClassFromSerializedData as readRootClassFromSerializedDataHelper,
  remapSerializedUuids as remapSerializedUuidsInClone,
  rewriteAssetPathsInClone,
} from './serializedDataHelpers.js';

import type { AssetPathReference, ClassNameReference } from './serializedDataHelpers.js';
import type { Actor } from '../actors/Actor.js';
import type { GameMode } from '../game/GameMode.js';
import type { World } from '../game/World.js';
import type { Resource } from '../utils/ResourceManager.js';
import type { SerializedData } from '../utils/serialization/types.js';
import type { Material } from 'three';

export type { AssetPathReference, ClassNameReference } from './serializedDataHelpers.js';
export { isLegacyData, serializationVersionKey } from '../utils/serialization/types.js';

/**
 * Checks if an object is a valid GameMode instance by checking its class registration.
 * This avoids the circular dependency from importing GameMode as a value.
 */
function isValidGameMode(obj: unknown): obj is GameMode {
  if (!obj || typeof obj !== 'object') return false;
  const className = ClassRegistry.getClassOrConstructorName(obj);
  if (!className) return false;
  // Check if it's registered as a GameMode or has GameMode in its class chain
  const GameModeClass = ClassRegistry.getClass('ENGINE.GameMode');
  if (!GameModeClass) return false;
  return obj instanceof GameModeClass;
}

/**
 * Scene resources configuration.
 */
export interface SceneResources {
  navMesh?: {
    path: string;
  };
  navMeshSettings?: {
    path: string;
  };
}

/**
 * World settings configuration.
 */
export interface WorldSettings {
  gameMode?: Record<string, unknown>; // Legacy format - kept for backward compatibility
  gameModeClass?: string; // New format - class name to instantiate
}

/**
 * Loader tags deserialized prefab nodes with `$prefab` for merge/diff. That marker makes
 * `getDefaultObject` return the cached prefab template as serialization defaults, which can
 * resurrect deleted children when re-exporting a prefab asset. Strip markers before prefab export.
 */
function stripPrefabTemplateMarkersForExport(value: unknown, visited: WeakSet<object>): void {
  if (!value || typeof value !== 'object') {
    return;
  }
  const o = value as Record<string, unknown>;
  if (visited.has(o)) {
    return;
  }
  visited.add(o);
  if ('$prefab' in o) {
    delete o.$prefab;
  }
  const children = (o as { children?: unknown }).children;
  if (Array.isArray(children)) {
    for (const c of children) {
      stripPrefabTemplateMarkersForExport(c, visited);
    }
  }
}


class WorldSerializationContext {
  // Loading context state
  private isEditor: boolean = false;

  /**
   * Exports an object to a serializable format.
   * @param object - The object to export
   * @param asPrefab - Whether it's exported as a prefab or regular storage
   * @param inlineToPrefab - Whether to inline to the actor's prefab itself, as against to creating a new prefab that is dependent on the actor's prefab
   * @returns The serialized object
   */
  public exportObject(object: object, asPrefab: boolean, inlineToPrefab: boolean = false): SerializedData {
    const flags = (
      DumperFlags.None
      | (asPrefab ? DumperFlags.AsPrefab : 0)
      | (inlineToPrefab ? DumperFlags.Inline : 0)
    );
    const dumper = createDumper({ flags });
    return dumper.dump(object);
  }

  // Loading methods
  /** Sets whether the context is in editor mode */
  public setEditorMode(isEditor: boolean): void {
    this.isEditor = isEditor;
  }

  private seedPrefabManager(prefabs: Record<string, SerializedData>): void {
    for (const [prefabPath, prefabData] of Object.entries(prefabs)) {
      PrefabManager.setPrefabData(prefabPath, prefabData);
    }
  }

  /**
   * Loads an actor from serialized data.
   * @param prefabs - A map of prefab names to their serialized data
   */
  public async loadActor(data: SerializedData, prefabs: Record<string, SerializedData> = {}): Promise<Actor | null> {
    if (isLegacyData(data)) {
      throw new Error('Legacy data is no longer supported');
    }

    this.seedPrefabManager(prefabs);
    const loader = createLoader({
      flags: this.isEditor ? LoaderFlags.None : LoaderFlags.WithoutEditor,
      reconcilePrefabInstanceChildren: true,
    });
    return await loader.loadAsync(data) as Actor;
  }

  /**
   * Loads an actor from serialized data, synchronous version.
   * @param prefabs - A map of prefab names to their serialized data
   */
  public loadActorSync(data: SerializedData, prefabs: Record<string, SerializedData> = {}): Actor | null {
    if (isLegacyData(data)) {
      throw new Error('Legacy data is no longer supported');
    }

    this.seedPrefabManager(prefabs);
    const loader = createLoader({
      flags: this.isEditor ? LoaderFlags.None : LoaderFlags.WithoutEditor,
      reconcilePrefabInstanceChildren: true,
    });
    return loader.load(data) as Actor;
  }

  /**
   * Loads world data into an existing world instance.
   * @returns The Level containing all loaded actors
   */
  public async loadWorld(world: World, data: SerializedData): Promise<Level> {
    if (isLegacyData(data)) {
      throw new Error('Legacy data is no longer supported');
    }

    // New format, use Loader
    const existingActors = new Set(world.getActors());
    const loader = createLoader({ reconcilePrefabInstanceChildren: true });
    await loader.loadToInstanceAsync(data, world);
    const loadedActors = world.getActors().filter(a => !existingActors.has(a));
    return new Level(world, loadedActors);
  }

  /** Imports an object from serialized data */
  public importObject(data: SerializedData): unknown {
    if (isLegacyData(data)) {
      throw new Error('Legacy data is no longer supported');
    }

    const loader = createLoader({ flags: this.isEditor ? LoaderFlags.None : LoaderFlags.WithoutEditor });
    return loader.load(data);
  }
}

/**
 * Handles loading and parsing of world data into the engine.
 */
export class WorldSerializer {
  /**
   * Exports an object to a serializable format.
   */
  public static exportObject(object: object): SerializedData {
    return new WorldSerializationContext().exportObject(object, false);
  }

  public static exportAsPrefab(obj: object, inlineToPrefab: boolean = false): SerializedData {
    const asPrefab = true;
    return new WorldSerializationContext().exportObject(obj, asPrefab, inlineToPrefab);
  }

  /**
   * Serializes a material as regular serialized data.
   */
  public static serializeMaterial(material: Material): SerializedData {
    return WorldSerializer.exportObject(material);
  }

  /**
   * Serializes a resource instance as regular serialized data.
   */
  public static serializeResource(resource: Resource): SerializedData {
    return WorldSerializer.exportObject(resource);
  }

  /**
   * Loads an actor from serialized data.
   * @param prefabs - A map of prefab names to their serialized data
   */
  public static async loadActor(actorData: SerializedData, prefabs: Record<string, SerializedData> = {}, isEditor?: boolean): Promise<Actor | null> {
    const context = new WorldSerializationContext();
    context.setEditorMode(isEditor ?? false);
    const loadedActor = context.loadActor(actorData, prefabs);
    return loadedActor;
  }

  /**
   * Loads an actor from serialized data, synchronous version.
   * @param prefabs - A map of prefab names to their serialized data. It must be provided if actorData contains a prefabName
   */
  public static loadActorSync(actorData: SerializedData, prefabs: Record<string, SerializedData> = {}, isEditor?: boolean): Actor | null {
    const context = new WorldSerializationContext();
    context.setEditorMode(isEditor ?? false);
    return context.loadActorSync(actorData, prefabs);
  }

  /**
   * Duplicates an actor by export -> UUID remap -> reload.
   */
  public static duplicateActor(
    actor: Actor,
    prefabs: Record<string, SerializedData> = {},
    isEditor: boolean = true
  ): Actor | null {
    try {
      const serializedData = WorldSerializer.exportObject(actor);
      const duplicatedData = cloneForDuplication(serializedData) as SerializedData;
      return WorldSerializer.loadActorSync(
        duplicatedData,
        prefabs,
        isEditor
      );
    } catch (error) {
      console.warn('[WorldSerializer] Failed to duplicate actor', error);
      return null;
    }
  }

  /**
   * Duplicates a scene component by export -> UUID remap -> import.
   */
  public static duplicateComponent(
    component: SceneComponent,
    isEditor: boolean = true
  ): SceneComponent | null {
    try {
      const serializedData = WorldSerializer.exportObject(component);
      const duplicatedData = cloneForDuplication(serializedData) as SerializedData;
      const duplicatedComponent = WorldSerializer.importObject(
        duplicatedData,
        isEditor
      );
      return duplicatedComponent instanceof SceneComponent ? duplicatedComponent : null;
    } catch (error) {
      console.warn('[WorldSerializer] Failed to duplicate component', error);
      return null;
    }
  }



  /**
   * Extracts world settings from serialized world data.
   * @returns The world settings, or null if none specified
   */
  private static getWorldSettings(sceneData: SerializedData): WorldSettings | null {
    // V2 format: settings are under $root
    if (sceneData.$root && typeof sceneData.$root === 'object' && !Array.isArray(sceneData.$root)) {
      const rootRecord = sceneData.$root as Record<string, unknown>;
      const settings = rootRecord.settings;
      if (settings && typeof settings === 'object' && !Array.isArray(settings)) {
        return settings as WorldSettings;
      }
    }

    // Legacy format: settings are at root level
    const legacySettings = (sceneData as unknown as { settings?: unknown }).settings;
    if (legacySettings && typeof legacySettings === 'object' && !Array.isArray(legacySettings)) {
      return legacySettings as WorldSettings;
    }

    return null;
  }

  /**
   * Extracts and instantiates the GameMode from scene data.
   * Call this before creating the World to get the GameMode for WorldOptions.
   * @returns The instantiated GameMode, or null if none specified in scene
   */
  public static extractGameMode(sceneData: SerializedData): GameMode | null {
    const settings = WorldSerializer.getWorldSettings(sceneData);

    // New format: gameModeClass is a string
    if (settings?.gameModeClass) {
      try {
        const GameModeClass = ClassRegistry.getClass(settings.gameModeClass);
        if (!GameModeClass) {
          console.warn(`[WorldSerializer] GameMode class not found: ${settings.gameModeClass}`);
          return null;
        }
        const gameModeFactory = GameModeClass as unknown as { create: (options?: object) => unknown };
        const gameMode = gameModeFactory.create({});
        // Validate that the instantiated object is a valid GameMode
        if (!isValidGameMode(gameMode)) {
          console.warn(`[WorldSerializer] Class ${settings.gameModeClass} is not a valid GameMode`);
          return null;
        }
        console.log(`[WorldSerializer] Extracted GameMode from scene: ${settings.gameModeClass}`);
        return gameMode;
      } catch (error) {
        console.warn(`[WorldSerializer] Failed to instantiate GameMode class: ${settings.gameModeClass}`, error);
        return null;
      }
    }

    // Legacy format: gameMode is a serialized object
    if (settings?.gameMode) {
      const legacyGameMode = settings.gameMode as Record<string, unknown>;

      // Try to extract class name from different legacy formats
      let className: string | null = null;

      // V2 format: { "$bc": "ClassName" }
      const legacyBc = legacyGameMode.$bc;
      if (typeof legacyBc === 'string' && legacyBc.length > 0) {
        className = legacyBc;
      }
      // V1 format: { "$base": { "$class": "ClassName" } }
      else {
        const legacyBase = legacyGameMode.$base;
        if (legacyBase && typeof legacyBase === 'object' && !Array.isArray(legacyBase)) {
          const baseClass = (legacyBase as { $class?: unknown }).$class;
          if (typeof baseClass === 'string' && baseClass.length > 0) {
            className = baseClass;
          }
        }
      }

      if (className) {
        try {
          const GameModeClass = ClassRegistry.getClass(className);
          if (!GameModeClass) {
            console.warn(`[WorldSerializer] GameMode class not found: ${className}`);
            return null;
          }
          const gameModeFactory = GameModeClass as unknown as { create: (options?: object) => unknown };
          const gameMode = gameModeFactory.create({});
          // Validate that the instantiated object is a valid GameMode
          if (!isValidGameMode(gameMode)) {
            console.warn(`[WorldSerializer] Class ${className} is not a valid GameMode (legacy format)`);
            return null;
          }
          console.log(`[WorldSerializer] Extracted GameMode from scene (legacy): ${className}`);
          return gameMode;
        } catch (error) {
          console.warn(`[WorldSerializer] Failed to instantiate GameMode class: ${className}`, error);
          return null;
        }
      }

      // Fallback: try to deserialize the whole object
      try {
        const loader = createLoader();
        const gameMode = loader.load({ $version: CurrentSerializationVersion, $root: legacyGameMode });
        if (!isValidGameMode(gameMode)) {
          console.warn('[WorldSerializer] Deserialized object is not a valid GameMode (loader fallback)');
          return null;
        }
        console.log('[WorldSerializer] Extracted GameMode from scene (loader fallback)');
        return gameMode;
      } catch (error) {
        console.warn('[WorldSerializer] Failed to deserialize GameMode from scene', error);
        return null;
      }
    }

    console.log('[WorldSerializer] No GameMode found in scene settings');
    return null;
  }

  /**
   * Loads world data into an existing world instance.
   * Creates actors, components, and scene hierarchies based on the provided data.
   * @returns The Level containing all loaded actors
   */
  public static async loadWorld(world: World, worldData: SerializedData, isEditor?: boolean): Promise<Level> {
    const context = new WorldSerializationContext();
    context.setEditorMode(isEditor ?? false);
    return context.loadWorld(world, worldData);
  }

  /** Imports an object from serialized data */
  public static importObject(objectData: SerializedData, isEditor?: boolean): unknown {
    const context = new WorldSerializationContext();
    context.setEditorMode(isEditor ?? false);
    return context.importObject(objectData);
  }

  /**
   * Creates prefab data from an actor.
   * @param inlineToPrefab - Whether to inline to the actor's prefab itself, as against to creating a new prefab that is dependent on the actor's prefab
   */
  public static createPrefabDataFromActor(actor: Actor, inlineToPrefab: boolean = false): SerializedData {
    const visited = new WeakSet<object>();
    stripPrefabTemplateMarkersForExport(actor, visited);
    stripPrefabTemplateMarkersForExport(actor.rootComponent, visited);
    return WorldSerializer.exportAsPrefab(actor, inlineToPrefab);
  }

  /** Creates prefab data from an actor with a parent prefab */
  public static createPrefabDataFromActorWithParent(
    actor: Actor,
    parentPrefabName: string
  ): SerializedData {
    const prefabData = actor.asExportedObject() as SerializedData & {
      prefabName?: string;
      parentPrefabName?: string;
    };
    replace_uuids(prefabData, { deleteKey: true });
    delete prefabData.prefabName;
    prefabData.parentPrefabName = parentPrefabName;
    return prefabData;
  }

  /** Loads the template actor from a prefab */
  public static loadPrefabTemplateActor(prefabName: string, prefabs: Record<string, SerializedData>, isEditor: boolean = true): Actor {
    const prefabData = prefabs[prefabName];
    if (isLegacyData(prefabData)) {
      throw new Error('Legacy data is no longer supported');
    }

    return loadPrefabTemplateInstance(prefabName, prefabs, { withoutEditor: !isEditor });
  }

  /**
   * Root class for serialized prefab/actor JSON: `$root.$bc` (including `$ref` + `$objects`) or legacy `class`.
   */
  public static readRootClassFromSerializedData(data: SerializedData): string | undefined {
    return readRootClassFromSerializedDataHelper(data);
  }

  /**
   * Lists prefab asset paths referenced by serialized graph data.
   */
  public static listPrefabPaths(data: SerializedData): string[] {
    return collectPrefabPathsFromSerializedData(data);
  }

  /**
   * Deep-clones `data` and assigns new UUIDs everywhere a `uuid` key appears (same as in-editor duplicate flow).
   */
  public static cloneWithNewUUIDs<T extends object>(data: T): T {
    return cloneActorWithNewUUIDs(data) as T;
  }

  /**
   * Deep-clones serialized data and rewrites `uuid` fields using `uuidMap` (from → to).
   */
  public static remapSerializedUuids<T extends object>(data: T, uuidMap: Record<string, string>): T {
    return remapSerializedUuidsInClone(data, uuidMap) as T;
  }

  /**
   * Recursively finds string values that match the asset `targetPath` (with `@project/` normalization).
   */
  public static scanAssetPathReferences(
    data: unknown,
    targetPath: string
  ): AssetPathReference[] {
    return collectAssetPathMatches(data, targetPath);
  }

  /**
   * Recursively finds class-name references (`$bc`, `$class`, or legacy `class`) whose value
   * equals `className`. Used to detect scenes/prefabs that depend on a JS class source file.
   */
  public static scanClassNameReferences(
    data: unknown,
    className: string
  ): ClassNameReference[] {
    return collectClassNameMatches(data, className);
  }

  /**
   * Returns a deep copy of `data` with all asset path string values matching `oldPath` rewritten
   * to `newPath` (including `@project/` normalization rules from the editor).
   */
  public static rewriteAssetPaths(
    data: unknown,
    oldPath: string,
    newPath: string
  ): unknown {
    return rewriteAssetPathsInClone(data, oldPath, newPath);
  }
}
